import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  DEPARTMENT_QUERY_KEYS,
  DESIGNATION_AREA_QUERY_KEYS,
  DESIGNATION_QUERY_KEYS,
  ENGAGEMENT_STATUS_QUERY_KEYS,
} from './keys';

import * as departmentService from 'services/department';
import * as designationService from 'services/designation';
import * as designationAreaService from 'services/designationArea';
import * as engagementStatusService from 'services/engagementStatus';

import type { ServiceParams } from 'interface/service';

type NamedItem = { id: number; name: string };

type ApiListResponse<T> = { data?: T[] };

const selectData = <T>(response: ApiListResponse<T>): T[] => response.data ?? [];

export const getDepartmentsQueryKey = () => [DEPARTMENT_QUERY_KEYS.ALL] as const;

export const departmentsQueryOptions = () => ({
  queryKey: getDepartmentsQueryKey(),
  queryFn: () => departmentService.fetchAll() as Promise<ApiListResponse<NamedItem>>,
});

export const useDepartmentsQuery = (enabled = true): UseQueryResult<NamedItem[], Error> => {
  return useQuery({
    ...departmentsQueryOptions(),
    select: selectData,
    enabled,
  });
};

export const getDesignationsQueryKey = (params?: ServiceParams) => [DESIGNATION_QUERY_KEYS.ALL, params] as const;

export const designationsQueryOptions = (params?: ServiceParams) => ({
  queryKey: getDesignationsQueryKey(params),
  queryFn: () => designationService.fetchAll() as Promise<ApiListResponse<NamedItem>>,
});

export const useDesignationsQuery = (enabled = true): UseQueryResult<NamedItem[], Error> => {
  return useQuery({
    ...designationsQueryOptions(),
    select: selectData,
    enabled,
  });
};

export const getDesignationAreasQueryKey = () => [DESIGNATION_AREA_QUERY_KEYS.ALL] as const;

export const designationAreasQueryOptions = () => ({
  queryKey: getDesignationAreasQueryKey(),
  queryFn: () => designationAreaService.fetchAll() as Promise<ApiListResponse<NamedItem>>,
});

export const useDesignationAreasQuery = (enabled = true): UseQueryResult<NamedItem[], Error> => {
  return useQuery({
    ...designationAreasQueryOptions(),
    select: selectData,
    enabled,
  });
};

export const getEngagementStatusesQueryKey = () => [ENGAGEMENT_STATUS_QUERY_KEYS.ALL] as const;

export const engagementStatusesQueryOptions = () => ({
  queryKey: getEngagementStatusesQueryKey(),
  queryFn: () => engagementStatusService.fetchAll() as Promise<ApiListResponse<NamedItem>>,
});

export const useEngagementStatusesQuery = (enabled = true): UseQueryResult<NamedItem[], Error> => {
  return useQuery({
    ...engagementStatusesQueryOptions(),
    select: selectData,
    enabled,
  });
};
